import { type Product, getFirstAttributeValue } from '@scayle/storefront-nuxt'
import { Action } from '../plugins/06.toast'

export const getWishlistToastMessage = (
  productName: string,
  addedToWishlist: boolean,
) => {
  const { $i18n } = useNuxtApp()
  return addedToWishlist
    ? $i18n.t('wishlist.notification.add_to_wishlist', {
        productName,
      })
    : $i18n.t('wishlist.notification.remove_from_wishlist', {
        productName,
      })
}

export const showWishlistToast = (
  isAddedToWishlist: boolean,
  item: Product | null,
) => {
  const { $alert, $i18n } = useNuxtApp()
  const name =
    getFirstAttributeValue(item?.attributes, 'name')?.label ||
    $i18n.t('wishlist.product')
  const message = getWishlistToastMessage(name, isAddedToWishlist)

  const action = isAddedToWishlist ? Action.route : Action.confirm

  $alert.show(
    message,
    action,
    isAddedToWishlist ? routeList.wishlist : undefined,
  )
}

// Wishlist toggle responses that are safe to treat as a no-op: the requested
// end state already holds (409 = already in list, 404 = already removed) or the
// request was rejected as too large (413). Swallowing these prevents an
// unhandled promise rejection from surfacing in Sentry (ECOM-2690).
const EXPECTED_WISHLIST_STATUS = new Set([404, 409, 413])

export const isExpectedWishlistError = (error: unknown): boolean => {
  const source = error as {
    statusCode?: number
    status?: number
    response?: { status?: number }
  } | null
  const status =
    source?.statusCode ?? source?.status ?? source?.response?.status
  return typeof status === 'number' && EXPECTED_WISHLIST_STATUS.has(status)
}

// Runs a wishlist toggle, swallowing the expected 404/409/413 responses as a
// no-op and rethrowing anything genuinely unexpected. Returns `true` when the
// toggle actually ran, `false` when an expected response was swallowed — the
// caller uses this to avoid emitting add/remove tracking for a state change
// that did not occur (ECOM-2690 / Bugbot review).
export const safeToggleWishlistItem = async <T>(
  toggle: (payload: T) => Promise<unknown>,
  payload: T,
): Promise<boolean> => {
  try {
    await toggle(payload)
    return true
  } catch (error) {
    if (!isExpectedWishlistError(error)) {
      throw error
    }
    // Expected end state already holds (or 413) — no-op, nothing changed.
    return false
  }
}
